import React, { useState } from 'react';
import {
	Box,
	Paper,
	Button,
	Typography,
	TextField,
	Chip,
	Stack,
	ToggleButton,
	ToggleButtonGroup,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from '@mui/material';
import { Iconify } from '../Minimal/src/components/iconify';
import { API_URL } from '../Config';
import { toast } from 'sonner';
import config from "../common/Config";
import { v4 as uuidv4 } from 'uuid';
import printPaymentReceipt from './printPaymentReceipt';

const Finish_Pay_Incomplete_Order = ({
	orderId,
	grandTotal,
	current_Bal,
	credit_Type_Name = '',
	prev_Due,
	fetchEposSalesTable
}) => {
	const accessToken = localStorage.getItem('accessToken');
	const userId = localStorage.getItem('userId');
	
	const [activePaymentMethod, setActivePaymentMethod] = useState('cash');
	const [payments, setPayments] = useState([]);
	const [amount, setAmount] = useState('');
	const [chequeNo, setChequeNo] = useState('');
	const [chequeDate, setChequeDate] = useState('');
	const [cardNumber, setCardNumber] = useState('');
	const [paidAmt, setPaidAmt] = useState(0.00);
	const [payDate] = useState(new Date().toISOString().split('T')[0]);
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [paymentToDelete, setPaymentToDelete] = useState(null);

	const handleButtonClick = (event, newMethod) => {
		if (newMethod !== null) {
			setActivePaymentMethod(newMethod);
			resetInputs();
		}
	};

	const resetInputs = () => {
		setAmount('');
		setChequeNo('');
		setChequeDate('');
		setCardNumber('');
	};

	const handleSave = () => {
		if (activePaymentMethod === 'onAccount') return;
		
		if (!amount || parseFloat(amount) < 0.01) {
			toast.warning('Please enter a valid amount greater than zero!');
			return;
		}

		const newPaidAmt = parseFloat(paidAmt) + parseFloat(amount || 0);
		setPaidAmt(newPaidAmt);
		
		const newPayment = {
			uid: uuidv4(),
			amount,
			method: activePaymentMethod,
			chequeNo: chequeNo,
			chequeDate,
			cardNumber,
		};

		setPayments([...payments, newPayment]);
		resetInputs();
	};

	const handleAmountCancel = (id) => {
		setPaymentToDelete(id);
		setDeleteDialogOpen(true);
	};

	const confirmDelete = () => {
		const payment = payments.find(p => p.uid === paymentToDelete);
		if (payment) {
			const newPaidAmt = parseFloat(paidAmt) - parseFloat(payment.amount || 0);
			setPaidAmt(newPaidAmt);
		}
		setPayments(payments.filter(p => p.uid !== paymentToDelete));
		setDeleteDialogOpen(false);
		setPaymentToDelete(null);
	};

	const clearTableData = () => {
		resetInputs();
		setPayments([]);
		setPaidAmt(0.00);
	};

	const handleCompleteOrder = async () => {
		try {
			let cashAmount = 0, chequeAmount = 0, cardAmount = 0;
			let firstChequeNo = '', firstChequeDate = '', firstCardNumber = '';
			
			payments.forEach(p => {
				if (p.method === 'cash') cashAmount += parseFloat(p.amount || 0);
				if (p.method === 'cheque') {
					chequeAmount += parseFloat(p.amount || 0);
					if (!firstChequeNo && p.chequeNo) firstChequeNo = p.chequeNo;
					if (!firstChequeDate && p.chequeDate) firstChequeDate = p.chequeDate;
				}
				if (p.method === 'card') {
					cardAmount += parseFloat(p.amount || 0);
					if (!firstCardNumber && p.cardNumber) firstCardNumber = p.cardNumber;
				}
			});

			const formData = new FormData();
			formData.append('user_id', userId);
			formData.append('date', payDate);
			formData.append('amount', activePaymentMethod === 'onAccount' ? 0 : paidAmt);
			formData.append('paid_by', activePaymentMethod);
			formData.append('sale_id', orderId);
			formData.append('cheque_no', firstChequeNo);
			formData.append('cheque_date', firstChequeDate);
			formData.append('card_number', firstCardNumber);
			formData.append('cash_amount', cashAmount.toFixed(2));
			formData.append('cheque_amount', chequeAmount.toFixed(2));
			formData.append('card_amount', cardAmount.toFixed(2));
			formData.append('payments', JSON.stringify(payments));

			const response = await fetch(`${config.API_URL}/pos_payment`, {
				method: 'POST',
				headers: {
					authorization: `Bearer ${accessToken}`,
				},
				body: formData,
			});

			if (!response.ok) {
				throw new Error('Network response was not ok');
			}
			
			const data = await response.json();
			
			if (data.success) {
				toast.success('Payment added successfully');

				const accessTokenValue = String(localStorage.getItem('accessToken') || '').toLowerCase();
				const role = String(localStorage.getItem('user_role') || '').trim();
				const isRhp =
					accessTokenValue === 'rhp' ||
					accessTokenValue === 'hp' ||
					role === '18' ||
					role === '19';

				if (!isRhp) {
					try {
						await printPaymentReceipt(orderId, payments, grandTotal, paidAmt);
					} catch (error) {
						console.error('Error generating payment receipt:', error);
					}
				}

				fetchEposSalesTable();
				document.querySelectorAll('.modal-backdrop').forEach((backdrop) => backdrop.remove());
				clearTableData();
			} else {
				toast.error('Order Complete Failed. Try Again');
			}
		} catch (error) {
			console.error('Error:', error);
			toast.error('An error occurred while processing payment');
		}
		setConfirmDialogOpen(false);
	};

	const formatCurrency = (value) => {
		const symbol = localStorage.getItem("currencySymbol") || '£';
		return `${symbol}${parseFloat(value || 0).toFixed(2)}`;
	};

	const balance = (paidAmt - parseFloat(grandTotal || 0)).toFixed(2);

	return (
		<div className="container">
			<div className="modal fade" aria-hidden="true" tabIndex="-1" role="dialog" id='modalId'>
				<div className="modal-dialog modal-dialog-centered modal-lg">
					<div className="modal-content" style={{ borderRadius: '8px', border: 'none' }}>
						<Box sx={{ p: 3 }}>
							<Paper
								elevation={0}
								sx={{
									p: 2.5,
									mb: 3,
									backgroundColor: '#f5f7fa',
									borderRadius: 2,
									border: '1px solid rgba(145, 158, 171, 0.16)',
								}}
							>
								<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
									<Box>
										<Typography sx={{ fontSize: '0.75rem', color: '#637381', mb: 0.5 }}>Invoice Total</Typography>
										<Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: '#212b36' }}>
											{formatCurrency(grandTotal)}
										</Typography>
									</Box>
									<Box>
										<Typography sx={{ fontSize: '0.75rem', color: '#637381', mb: 0.5 }}>Paid</Typography>
										<Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: '#2e7d32' }}>
											{formatCurrency(paidAmt)}
										</Typography>
									</Box>
									<Box>
										<Typography sx={{ fontSize: '0.75rem', color: '#637381', mb: 0.5 }}>Balance</Typography>
										<Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: parseFloat(balance) >= 0 ? '#2e7d32' : '#d32f2f' }}>
											{formatCurrency(balance)}
										</Typography>
									</Box>
								</Stack>
							</Paper>

							<Paper
								elevation={0}
								sx={{
									borderRadius: 2,
									border: '1px solid rgba(145, 158, 171, 0.16)',
									overflow: 'hidden',
									mb: 3,
								}}
							>
								<Box sx={{ overflowX: 'auto', maxHeight: 200 }}>
									<table style={{ width: '100%', borderCollapse: 'collapse' }}>
										<thead>
											<tr style={{ backgroundColor: '#f5f7fa' }}>
												<th style={{ padding: '12px', fontSize: '0.75rem', fontWeight: 600, color: '#212b36', textAlign: 'left', borderBottom: '1px solid rgba(145, 158, 171, 0.16)' }}>No</th>
												<th style={{ padding: '12px', fontSize: '0.75rem', fontWeight: 600, color: '#212b36', textAlign: 'right', borderBottom: '1px solid rgba(145, 158, 171, 0.16)' }}>Paid</th>
												<th style={{ padding: '12px', fontSize: '0.75rem', fontWeight: 600, color: '#212b36', textAlign: 'right', borderBottom: '1px solid rgba(145, 158, 171, 0.16)' }}>Mode</th>
												<th style={{ padding: '12px', fontSize: '0.75rem', fontWeight: 600, color: '#212b36', textAlign: 'left', borderBottom: '1px solid rgba(145, 158, 171, 0.16)' }}>Details</th>
												<th style={{ padding: '12px', fontSize: '0.75rem', fontWeight: 600, color: '#212b36', textAlign: 'center', borderBottom: '1px solid rgba(145, 158, 171, 0.16)' }}>Action</th>
											</tr>
										</thead>
										<tbody>
											{payments.length === 0 ? (
												<tr>
													<td colSpan="5" style={{ padding: '32px', textAlign: 'center' }}>
														<Typography sx={{ color: '#637381', fontSize: '0.875rem' }}>
															No payments added yet
														</Typography>
													</td>
												</tr>
											) : (
												payments.map((payment, index) => (
													<tr key={payment.uid} style={{ borderBottom: '1px solid rgba(145, 158, 171, 0.16)' }}>
														<td style={{ padding: '12px', fontSize: '0.8rem', color: '#212b36' }}>{index + 1}</td>
														<td style={{ padding: '12px', fontSize: '0.8rem', color: '#212b36', textAlign: 'right', fontWeight: 500 }}>
															{formatCurrency(payment.amount)}
														</td>
														<td style={{ padding: '12px', fontSize: '0.8rem', textAlign: 'right' }}>
															<Chip
																label={payment.method.charAt(0).toUpperCase() + payment.method.slice(1)}
																size="small"
																sx={{
																	backgroundColor: '#e3f2fd',
																	color: '#1976d2',
																	fontWeight: 500,
																	fontSize: '0.75rem',
																	height: 24,
																}}
															/>
														</td>
														<td style={{ padding: '12px', fontSize: '0.8rem', color: '#637381' }}>
															{payment.chequeNo || payment.cardNumber || '--'}
														</td>
														<td style={{ padding: '12px', textAlign: 'center' }}>
															<IconButton
																size="small"
																onClick={() => handleAmountCancel(payment.uid)}
																sx={{ color: '#d32f2f' }}
															>
																<Iconify icon="solar:trash-bin-trash-bold" width={20} />
															</IconButton>
														</td>
													</tr>
												))
											)}
										</tbody>
									</table>
								</Box>
							</Paper>

							{activePaymentMethod === 'cash' && (
								<Stack direction="row" spacing={2} sx={{ mb: 3 }}>
									<TextField
										label="Amount"
										type="number"
										fullWidth
										value={amount}
										onChange={(e) => {
											const value = e.target.value.replace(/[^0-9.]/g, '');
											const finalValue = value.indexOf('.') !== -1 ? value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1') : value;
											setAmount(finalValue);
										}}
										sx={{
											"& .MuiOutlinedInput-root": {
												backgroundColor: "#f5f7fa",
											}
										}}
									/>
									<Button
										variant="contained"
										onClick={handleSave}
										sx={{
											textTransform: "none",
											fontWeight: 500,
											boxShadow: "none",
											minWidth: 100,
											'&:hover': {
												boxShadow: "none"
											}
										}}
									>
										Save
									</Button>
								</Stack>
							)}

							{activePaymentMethod === 'cheque' && (
								<Stack spacing={2} sx={{ mb: 3 }}>
									<Stack direction="row" spacing={2}>
										<TextField
											label="Amount"
											type="number"
											fullWidth
											value={amount}
											onChange={(e) => {
												const value = e.target.value.replace(/[^0-9.]/g, '');
												const finalValue = value.indexOf('.') !== -1 ? value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1') : value;
												setAmount(finalValue);
											}}
											sx={{
												"& .MuiOutlinedInput-root": {
													backgroundColor: "#f5f7fa",
												}
											}}
										/>
										<TextField
											label="Cheque No"
											fullWidth
											value={chequeNo}
											onChange={(e) => setChequeNo(e.target.value)}
											sx={{
												"& .MuiOutlinedInput-root": {
													backgroundColor: "#f5f7fa",
												}
											}}
										/>
									</Stack>
									<Stack direction="row" spacing={2}>
										<TextField
											label="Cheque Date"
											type="date"
											fullWidth
											value={chequeDate}
											onChange={(e) => setChequeDate(e.target.value)}
											slotProps={{
												inputLabel: { shrink: true }
											}}
											sx={{
												"& .MuiOutlinedInput-root": {
													backgroundColor: "#f5f7fa",
												}
											}}
										/>
										<Button
											variant="contained"
											onClick={handleSave}
											sx={{
												textTransform: "none",
												fontWeight: 500,
												boxShadow: "none",
												minWidth: 100,
												'&:hover': {
													boxShadow: "none"
												}
											}}
										>
											Save
										</Button>
									</Stack>
								</Stack>
							)}

							{activePaymentMethod === 'card' && (
								<Stack direction="row" spacing={2} sx={{ mb: 3 }}>
									<TextField
										label="Amount"
										type="number"
										fullWidth
										value={amount}
										onChange={(e) => {
											const value = e.target.value.replace(/[^0-9.]/g, '');
											const finalValue = value.indexOf('.') !== -1 ? value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1') : value;
											setAmount(finalValue);
										}}
										sx={{
											"& .MuiOutlinedInput-root": {
												backgroundColor: "#f5f7fa",
											}
										}}
									/>
									<TextField
										label="Transaction ID"
										fullWidth
										value={cardNumber}
										onChange={(e) => setCardNumber(e.target.value)}
										sx={{
											"& .MuiOutlinedInput-root": {
												backgroundColor: "#f5f7fa",
											}
										}}
									/>
									<Button
										variant="contained"
										onClick={handleSave}
										sx={{
											textTransform: "none",
											fontWeight: 500,
											boxShadow: "none",
											minWidth: 100,
											'&:hover': {
												boxShadow: "none"
											}
										}}
									>
										Save
									</Button>
								</Stack>
							)}

							{activePaymentMethod === 'onAccount' && (
								<Paper
									elevation={0}
									sx={{
										p: 2.5,
										mb: 3,
										backgroundColor: '#f5f7fa',
										borderRadius: 2,
										border: '1px solid rgba(145, 158, 171, 0.16)',
									}}
								>
									{credit_Type_Name && credit_Type_Name !== 'undefined' ? (
										<Stack spacing={1.5}>
											<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
												<Typography sx={{ fontSize: '0.875rem', color: '#637381' }}>Credit Type:</Typography>
												<Typography sx={{ fontSize: '0.875rem', color: '#212b36', fontWeight: 500 }}>
													{credit_Type_Name}
												</Typography>
											</Box>
											<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
												<Typography sx={{ fontSize: '0.875rem', color: '#637381' }}>Current Balance:</Typography>
												<Typography sx={{ fontSize: '0.875rem', color: '#212b36', fontWeight: 500 }}>
													{formatCurrency(current_Bal)}
												</Typography>
											</Box>
											<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
												<Typography sx={{ fontSize: '0.875rem', color: '#637381' }}>Amount Due:</Typography>
												<Typography sx={{ fontSize: '0.875rem', color: '#212b36', fontWeight: 500 }}>
													{formatCurrency(prev_Due)}
												</Typography>
											</Box>
										</Stack>
									) : (
										<Typography sx={{ fontSize: '0.875rem', color: '#d32f2f', fontWeight: 600, textAlign: 'center' }}>
											No Credit Facility
										</Typography>
									)}
								</Paper>
							)}

							<Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
								<ToggleButtonGroup
									value={activePaymentMethod}
									exclusive
									onChange={handleButtonClick}
									sx={{
										'& .MuiToggleButton-root': {
											textTransform: 'none',
											fontWeight: 500,
											px: 3,
											py: 1,
											'&.Mui-selected': {
												backgroundColor: '#1976d2',
												color: '#fff',
												'&:hover': {
													backgroundColor: '#1565c0',
												}
											}
										}
									}}
								>
									<ToggleButton value="cash">Cash</ToggleButton>
									<ToggleButton value="cheque">Cheque</ToggleButton>
									<ToggleButton value="card">Card</ToggleButton>
									<ToggleButton value="onAccount">Credit Facility</ToggleButton>
								</ToggleButtonGroup>
							</Box>

							<Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
								<Button
									onClick={clearTableData}
									data-bs-dismiss="modal"
									sx={{
										textTransform: "none",
										fontWeight: 500,
										color: "#637381",
										px: 3,
									}}
								>
									Cancel
								</Button>
								<Button
									variant="contained"
									onClick={() => setConfirmDialogOpen(true)}
									disabled={
										(activePaymentMethod === 'onAccount' && (!credit_Type_Name || credit_Type_Name === 'undefined')) ||
										(activePaymentMethod !== 'onAccount' && paidAmt < grandTotal)
									}
									sx={{
										textTransform: "none",
										fontWeight: 500,
										boxShadow: "none",
										px: 3,
										'&:hover': {
											boxShadow: "none"
										}
									}}
								>
									Save & Print
								</Button>
							</Box>
						</Box>
					</div>
				</div>
			</div>

			<Dialog
				open={confirmDialogOpen}
				onClose={() => setConfirmDialogOpen(false)}
				PaperProps={{
					sx: {
						borderRadius: 2,
					},
				}}
			>
				<DialogTitle sx={{ fontWeight: 600, fontSize: "1.125rem", color: "#212b36" }}>
					Confirm Payment
				</DialogTitle>
				<DialogContent>
					<Typography sx={{ color: "#637381" }}>
						Do you want to make a payment?
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 3, py: 2 }}>
					<Button
						onClick={() => setConfirmDialogOpen(false)}
						sx={{
							textTransform: "none",
							fontWeight: 500,
							color: "#637381",
						}}
					>
						Cancel
					</Button>
					<Button
						onClick={handleCompleteOrder}
						variant="contained"
						data-bs-dismiss="modal"
						sx={{
							textTransform: "none",
							fontWeight: 500,
							boxShadow: "none",
							'&:hover': {
								boxShadow: "none"
							}
						}}
					>
						Yes, Confirm
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={deleteDialogOpen}
				onClose={() => setDeleteDialogOpen(false)}
				PaperProps={{
					sx: {
						borderRadius: 2,
					},
				}}
			>
				<DialogTitle sx={{ fontWeight: 600, fontSize: "1.125rem", color: "#212b36" }}>
					Delete Payment
				</DialogTitle>
				<DialogContent>
					<Typography sx={{ color: "#637381" }}>
						This action will remove the payment and update the total amount. Are you sure?
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 3, py: 2 }}>
					<Button
						onClick={() => setDeleteDialogOpen(false)}
						sx={{
							textTransform: "none",
							fontWeight: 500,
							color: "#637381",
						}}
					>
						No, Cancel
					</Button>
					<Button
						onClick={confirmDelete}
						variant="contained"
						color="error"
						sx={{
							textTransform: "none",
							fontWeight: 500,
							boxShadow: "none",
							'&:hover': {
								boxShadow: "none"
							}
						}}
					>
						Yes, Delete
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default Finish_Pay_Incomplete_Order;
